import {
	getFormProps,
	getInputProps,
	getTextareaProps,
	useForm,
} from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { and, count, eq, isNull } from 'drizzle-orm'
import { Form, Link, data, useActionData, useLoaderData } from 'react-router'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { redirectBack } from 'remix-utils/redirect-back'
import { safeRedirect } from 'remix-utils/safe-redirect'
import { z } from 'zod'
import { Field, TextareaField, ErrorList } from '#app/components/forms.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { pageDefinitions, users } from '#app/db/schema.ts'
import {
	getActiveEntitlement,
	getUserUsageSummary,
} from '#app/lighthouse/services/usage.service.ts'
import { getPersonalOrg } from '#app/services/organization.service.ts'
import { getUserId } from '#app/utils/auth.server.ts'
import { db } from '#app/utils/db.server.ts'
import { sendEmail } from '#app/utils/email.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { redirectWithToast } from '#app/utils/toast.server.ts'
import { useIsPending } from '#app/utils/use-pending.tsx'
import { type Route } from './+types/support.ts'
import {
	type CustomerInfo,
	SupportConfirmationEmail,
	SupportNotificationEmail,
} from './support.emails.tsx'

const SupportFormSchema = z.object({
	email: z
		.string({ required_error: 'Email is required' })
		.email('Please enter a valid email'),
	message: z
		.string({ required_error: 'Message is required' })
		.min(10, 'Please write at least 10 characters'),
})

export const meta: Route.MetaFunction = () => [
	{ title: 'Support | xcelera' },
	{
		name: 'description',
		content:
			'Get help with xcelera. Send us a message and we will reply within 24 hours.',
	},
]

export async function loader({ request }: Route.LoaderArgs) {
	const userId = await getUserId(request)
	const user = userId
		? await db
				.select({ email: users.email })
				.from(users)
				.where(eq(users.id, userId))
				.get()
		: null

	const backResponse = redirectBack(request, { fallback: '/' })
	const location = new Headers(backResponse.init?.headers).get('Location')
	const backTo = safeRedirect(location, '/')

	return { defaultEmail: user?.email ?? null, backTo }
}

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	await checkHoneypot(formData)

	const submission = parseWithZod(formData, {
		schema: SupportFormSchema,
	})

	if (submission.status !== 'success') {
		return data(
			{ result: submission.reply(), status: 'error' as const },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	const { email, message } = submission.value

	const userId = await getUserId(request)
	let customerInfo: CustomerInfo | null = null
	const personalOrg = userId ? await getPersonalOrg(userId) : null
	if (userId && personalOrg) {
		const organizationId = personalOrg.id
		const [user, entitlement, usage, pageCountResult] = await Promise.all([
			db
				.select({ name: users.name, createdAt: users.createdAt })
				.from(users)
				.where(eq(users.id, userId))
				.get(),
			getActiveEntitlement(organizationId),
			getUserUsageSummary(organizationId),
			db
				.select({ value: count() })
				.from(pageDefinitions)
				.where(
					and(
						eq(pageDefinitions.organizationId, organizationId),
						isNull(pageDefinitions.deletedAt),
					),
				)
				.get(),
		])
		const pageCount = pageCountResult?.value ?? 0
		if (user) {
			customerInfo = {
				name: user.name,
				memberSince: user.createdAt,
				tier: entitlement.tier,
				creditsUsed: usage.creditsUsed,
				creditsTotal: usage.creditsTotal,
				pageCount,
			}
		}
	}

	await Promise.all([
		sendEmail({
			to: 'support@xcelera.dev',
			subject: `[Support] New message`,
			react: (
				<SupportNotificationEmail
					email={email}
					message={message}
					customerInfo={customerInfo}
				/>
			),
		}),
		sendEmail({
			to: email,
			subject: 'We got your message — xcelera',
			react: <SupportConfirmationEmail />,
		}),
	])

	if (userId) {
		const backResponse = redirectBack(request, { fallback: '/' })
		const location = new Headers(backResponse.init?.headers).get('Location')
		const redirectTo = safeRedirect(location, '/')

		return redirectWithToast(redirectTo, {
			type: 'success',
			title: 'Message sent!',
			description: "We'll reply within 24 hours or less.",
		})
	}

	return data({
		result: submission.reply({ resetForm: true }),
		status: 'success' as const,
	})
}

export default function SupportRoute() {
	const isPending = useIsPending()
	const actionData = useActionData<typeof action>()
	const loaderData = useLoaderData<typeof loader>()

	const [form, fields] = useForm({
		id: 'support-form',
		constraint: getZodConstraint(SupportFormSchema),
		lastResult: actionData?.result,
		defaultValue: {
			email: loaderData.defaultEmail ?? '',
		},
		shouldRevalidate: 'onBlur',
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: SupportFormSchema })
		},
	})

	return (
		<main className="container mx-auto max-w-2xl px-4 py-16">
			<div className="space-y-8">
				<div className="text-center">
					<h1 className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
						Need help or have a question?
					</h1>
					<p className="text-muted-foreground mx-auto mt-4 max-w-xl text-lg leading-relaxed">
						Send us a message — we usually reply within 24 hours or less.
					</p>
				</div>

				{actionData?.status === 'success' ? (
					<div className="bg-muted/50 rounded-lg border p-8 text-center">
						<div className="flex flex-col items-center gap-6">
							<div className="bg-muted flex size-12 items-center justify-center rounded-full">
								<Icon name="check" className="size-6 text-green-500" />
							</div>
							<div className="space-y-2">
								<h2 className="text-foreground text-xl font-semibold">
									Message sent
								</h2>
								<p className="text-muted-foreground max-w-md">
									We'll reply by email — usually within 24 hours. In the
									meantime, check out what xcelera can do for you.
								</p>
							</div>
							<div className="flex flex-wrap justify-center gap-3">
								<Link
									to="/docs"
									className="text-muted-foreground hover:text-foreground text-sm font-medium underline underline-offset-4 transition-colors"
								>
									Read the docs
								</Link>
								<span className="text-muted-foreground/50">·</span>
								<Link
									to="/pricing"
									className="text-muted-foreground hover:text-foreground text-sm font-medium underline underline-offset-4 transition-colors"
								>
									See pricing
								</Link>
								<span className="text-muted-foreground/50">·</span>
								<Link
									to="/signup"
									className="text-muted-foreground hover:text-foreground text-sm font-medium underline underline-offset-4 transition-colors"
								>
									Create an account
								</Link>
							</div>
						</div>
					</div>
				) : (
					<>
						<p className="text-muted-foreground text-center text-sm">
							Quick answers might already be here:{' '}
							<Link
								to="/docs"
								className="text-foreground underline underline-offset-4"
							>
								Docs
							</Link>
						</p>

						<div className="bg-muted/50 rounded-lg border p-8">
							<Form method="post" className="space-y-2" {...getFormProps(form)}>
								<HoneypotInputs />

								<Field
									labelProps={{ children: 'Email' }}
									inputProps={{
										...getInputProps(fields.email, { type: 'email' }),
										autoComplete: 'email',
										placeholder: 'you@example.com',
									}}
									errors={fields.email.errors}
								/>

								<TextareaField
									labelProps={{ children: 'Message' }}
									textareaProps={{
										...getTextareaProps(fields.message),
										rows: 4,
										className: 'field-sizing-fixed',
									}}
									errors={fields.message.errors}
								/>

								<ErrorList errors={form.errors} id={form.errorId} />

								<div className="flex items-center justify-between">
									<Link
										to={loaderData.backTo}
										className="text-muted-foreground hover:text-foreground flex gap-2 text-sm font-semibold transition-colors"
									>
										<Icon name="reset" size="sm" />
										<span>Go back</span>
									</Link>
									<StatusButton
										status={isPending ? 'pending' : (form.status ?? 'idle')}
										type="submit"
										disabled={isPending}
									>
										Send message
									</StatusButton>
								</div>
							</Form>
						</div>
					</>
				)}
			</div>
		</main>
	)
}
